import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  FlaskConical,
  BookOpen,
  LayoutDashboard,
  Menu,
  X,
  Atom,
  Globe,
  Sun,
  Moon,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";

const NAV_ITEMS = [
  { labelKey: "nav.llms", href: "/llms", icon: <FlaskConical size={15} />, descKey: "nav.llms.desc" },
  { labelKey: "nav.docs", href: "/docs", icon: <BookOpen size={15} />, descKey: "nav.docs.desc" },
];

export default function Navbar() {
  const [location] = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const { platformTheme, setPlatformTheme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isConsole = location.startsWith("/console");

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-[100] transition-all duration-300",
        scrolled || isConsole ? "nav-glass shadow-sm" : "bg-transparent"
      )}
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group flex-shrink-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:shadow-blue-500/40 transition-all duration-300 group-hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #2563EB 0%, #06B6D4 50%, #818CF8 100%)' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 3h6v8l4 7H5l4-7V3z" />
                <path d="M9 3h6" />
                <circle cx="12" cy="15" r="1" fill="white" stroke="none" />
                <circle cx="10" cy="13" r="0.5" fill="white" stroke="none" />
                <circle cx="14" cy="14" r="0.5" fill="white" stroke="none" />
              </svg>
            </div>
            <span className="font-bold text-lg text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Science<span className="text-blue-400">Token</span>
            </span>
            <span className="self-start mt-1 text-[9px] font-bold tracking-wider uppercase leading-none px-1.5 py-0.5 rounded-md bg-amber-500/15 text-amber-400 border border-amber-500/30">
              Beta
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1 flex-nowrap">
            {NAV_ITEMS.map((item) => {
              const isActive = location === item.href || location.startsWith(item.href + "/");
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "relative flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap",
                    isActive
                      ? "text-white bg-white/10"
                      : "text-slate-400 hover:text-white hover:bg-white/5"
                  )}
                >
                  {item.icon}
                  {t(item.labelKey)}
                  {isActive && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-blue-400" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Right Actions */}
          <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
            {/* Uptime dot */}
            <div className="relative group mr-1">
              <span className="status-dot cursor-default" style={{ width: 6, height: 6 }} />
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 px-2 py-1 rounded-md bg-slate-800 border border-white/10 text-[10px] text-emerald-400 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                {t("nav.uptime")}
              </div>
            </div>

            <div className="w-px h-4 bg-white/10 mx-1" />

            {/* Language Switcher */}
            <button
              onClick={() => setLanguage(language === "zh" ? "en" : "zh")}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all duration-200"
              title={language === "zh" ? "Switch to English" : "切换到中文"}
            >
              <Globe size={14} />
              <span className="text-xs font-medium">{language === "zh" ? "中" : "EN"}</span>
            </button>
            {/* Theme Toggle */}
            <button
              onClick={() => setPlatformTheme(platformTheme === "tech" ? "fresh" : "tech")}
              className="flex items-center p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all duration-200"
              title={platformTheme === "tech" ? "Switch to Light" : "Switch to Dark"}
            >
              {platformTheme === "tech" ? <Sun size={14} /> : <Moon size={14} />}
            </button>

            <div className="w-px h-4 bg-white/10 mx-1" />

            {isAuthenticated ? (
              <>
                <Link href="/console/dashboard">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-300 hover:text-white hover:bg-white/10 gap-1.5 text-sm h-8 px-3 rounded-lg"
                  >
                    <LayoutDashboard size={14} />
                    {t("nav.console")}
                  </Button>
                </Link>
                <div className="flex items-center gap-2 ml-1">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-xs font-bold text-white">
                    {user?.display_name?.charAt(0).toUpperCase() || "U"}
                  </div>
                  <button
                    onClick={logout}
                    className="flex items-center p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all duration-200"
                    title="Logout"
                  >
                    <LogOut size={14} />
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link href="/login">
                  <Button
                    size="sm"
                    className="btn-primary-sci text-sm font-semibold px-4 h-8 rounded-lg"
                  >
                    {t("nav.login")}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden nav-glass border-t border-white/5">
          <div className="px-4 py-4 space-y-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                  location === item.href
                    ? "text-white bg-white/10"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                )}
              >
                {item.icon}
                <div>
                  <div>{t(item.labelKey)}</div>
                  <div className="text-xs text-slate-500">{t(item.descKey)}</div>
                </div>
              </Link>
            ))}
            {/* Mobile Language Switcher */}
            <button
              onClick={() => setLanguage(language === "zh" ? "en" : "zh")}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-slate-400 hover:text-white"
            >
              <Globe size={14} />
              {language === "zh" ? "Switch to English" : "切换到中文"}
            </button>
            {/* Mobile Theme Toggle */}
            <button
              onClick={() => setPlatformTheme(platformTheme === "tech" ? "fresh" : "tech")}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-slate-400 hover:text-white"
            >
              {platformTheme === "tech" ? <Sun size={14} /> : <Moon size={14} />}
              {platformTheme === "tech" ? (language === "zh" ? "切换到清新风" : "Switch to Light") : (language === "zh" ? "切换到科技风" : "Switch to Dark")}
            </button>
            <div className="pt-3 flex flex-col gap-2">
              {isAuthenticated ? (
                <Link href="/console/dashboard" onClick={() => setMobileOpen(false)}>
                  <Button variant="ghost" size="sm" className="w-full text-slate-300 hover:text-white justify-start gap-2">
                    <LayoutDashboard size={14} />
                    {t("nav.console")}
                  </Button>
                </Link>
              ) : (
                <Link href="/login" onClick={() => setMobileOpen(false)}>
                  <Button size="sm" className="w-full btn-primary-sci">
                    {t("nav.login")}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
